import { useState, useEffect, useRef } from 'react';

export function useLayoutMode(containerRef) {
    const [layoutMode, setLayoutMode] = useState('mobile');
    const debounceTimer = useRef(null);

    useEffect(() => {
        if (!containerRef?.current) return;

        // Check if ResizeObserver is supported (not available on older iOS/Safari)
        if (typeof ResizeObserver === 'undefined') {
            console.warn('[useLayoutMode] ResizeObserver not supported, using fallback to window resize');

            // Fallback: Use window resize listener and measure container width
            const updateLayout = () => {
                clearTimeout(debounceTimer.current);
                debounceTimer.current = setTimeout(() => {
                    if (containerRef.current) {
                        const width = containerRef.current.offsetWidth;
                        if (width < 768) {
                            setLayoutMode('mobile');
                        } else if (width < 1200) {
                            setLayoutMode('tablet');
                        } else {
                            setLayoutMode('desktop');
                        }
                    }
                }, 100);
            };

            // Initial check
            updateLayout();

            // Listen to window resize
            window.addEventListener('resize', updateLayout);

            return () => {
                window.removeEventListener('resize', updateLayout);
                clearTimeout(debounceTimer.current);
            };
        }

        // Modern browsers: Use ResizeObserver
        const observer = new ResizeObserver(entries => {
            clearTimeout(debounceTimer.current);

            debounceTimer.current = setTimeout(() => {
                const width = entries[0].contentRect.width;

                if (width < 768) {
                    setLayoutMode('mobile');
                } else if (width < 1200) {
                    setLayoutMode('tablet');
                } else {
                    setLayoutMode('desktop');
                }
            }, 100);
        });

        observer.observe(containerRef.current);

        return () => {
            observer.disconnect();
            clearTimeout(debounceTimer.current);
        };
    }, [containerRef]);

    return layoutMode;
}
